/** @jsx jsx */

import { ComponentProps } from 'react';

import { Colors, Zindexes } from '@zapier/theme';
import { createKeyHandler } from '@zapier/common-utils';
import {
  Dropdown,
  DropdownDetails,
  Gravatar,
  OutsideClickable,
} from '@zapier/common-components';
import { css, jsx, Interpolation } from '@emotion/core';
import { HeaderUser } from '@zapier/product-dev-platform/src/types';

import NavLink from './NavLink';

import type { NavItem } from './types';

const avatarWrapCss: Interpolation = {
  display: 'flex',
  cursor: 'pointer',
  userSelect: 'none',
};

const dropdownLinkCss = (isOnlyMobile?: boolean) =>
  css(
    {
      minWidth: 237,
      a: {
        display: 'block',
        color: Colors.batmobile,
        padding: '9px 18px',
        ':hover': {
          backgroundColor: Colors.adamantium,
          color: Colors.batmobile,
        },
      },
    },
    isOnlyMobile && {
      '@media (min-width: 1079px)': {
        display: 'none',
      },
    }
  );

type Props = {
  ctaLinks: ComponentProps<typeof NavLink>[];
  isOpen: boolean;
  onClickLogout?: () => void;
  onClickZapier?: () => void;
  onToggle: () => void;
  optionsLinks: NavItem[];
  user: HeaderUser;
};

const UserOptions = ({
  ctaLinks,
  isOpen,
  onClickLogout,
  onClickZapier,
  onToggle,
  optionsLinks,
  user,
}: Props) => {
  return (
    <OutsideClickable isListening={isOpen} onClickOutside={onToggle}>
      <DropdownDetails
        isOpen={isOpen}
        summaryNode={
          <div
            aria-label={user.name}
            css={avatarWrapCss}
            onClick={e => {
              /**
               * We must event.preventDefault() because there is an issue syncing react states with native html <detail [open]> state
               * Most likely the synthetic event is triggering <detail [open]> again after the native event fires
               * https://github.com/facebook/react/issues/15486
               **/
              e.preventDefault();
              onToggle();
            }}
            onKeyDown={createKeyHandler(['enter'], onToggle)}
            role="button"
            tabIndex={0}
          >
            <Gravatar name={user.name} size={33} url={user.avatarUrl} />
          </div>
        }
      >
        <nav aria-label="Navigation of user options">
          <Dropdown
            horizontal="right"
            maxHeight="20em"
            tag="ul"
            vertical="bottom"
            zIndex={Zindexes.popover}
          >
            {ctaLinks.map(ctaLink => (
              <li css={dropdownLinkCss(true)} key={ctaLink.label}>
                <NavLink
                  href={ctaLink.href}
                  isExit={ctaLink.isExit}
                  label={ctaLink.label}
                />
              </li>
            ))}
            <li css={dropdownLinkCss(false)}>
              <NavLink
                href="https://www.zapier.com"
                isExit={true}
                label="zapier.com"
                onClick={onClickZapier}
              />
            </li>
            {optionsLinks.map(
              ({ label, href, isExit, onClick, onlyMobile }) => (
                <li css={dropdownLinkCss(onlyMobile)} key={label}>
                  <NavLink
                    href={href}
                    isExit={isExit}
                    label={label}
                    onClick={onClick}
                  />
                </li>
              )
            )}
            <li css={{ borderTop: `1px solid ${Colors.adamantium}` }} />
            <li css={dropdownLinkCss(false)}>
              <NavLink
                href="/logout"
                isExit={false}
                label="Log Out"
                onClick={onClickLogout}
              />
            </li>
          </Dropdown>
        </nav>
      </DropdownDetails>
    </OutsideClickable>
  );
};

UserOptions.defaultProps = {
  ctaLinks: [],
  optionsLinks: [],
};

export default UserOptions;
